import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { TextField, TextFieldVariant } from "@jmc/solid-design-system/src/components/atoms/TextField/TextField";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { mdiClose, mdiMagnify } from "@mdi/js";
import { useLocation } from "@reach/router";
import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchBox, UseSearchBoxProps } from "react-instantsearch";

import style from "./style.module.scss";

export const MIN_SEARCH_CHARS = 3;

interface PropTypes extends UseSearchBoxProps {
    showSearchButton?: boolean;
    queryStringAware?: boolean;
    variant?: "normal" | "big";
    placeholder?: string;
    initialQuery?: string;
    hasFocus?: boolean;
}

export const CustomSearchBox = (props: PropTypes): JSX.Element => {
    const { t } = useTranslation();
    const {
        showSearchButton = true,
        queryStringAware = false,
        variant = "normal",
        placeholder = t("Search by keyword", { ns: "search" }),
        initialQuery = "",
        hasFocus = false,
    } = props;
    const { refine, query, clear } = useSearchBox(props);
    const location = useLocation();
    const textFieldReference = useRef(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            refine(initialQuery);
            setInitialized(true);
        }
    }, [initialQuery]);

    const search = (): void => {
        refine(textFieldReference.current.value);
    };

    const onChange = (): void => {
        if (!showSearchButton) {
            if (textFieldReference?.current?.value?.length >= MIN_SEARCH_CHARS)
                refine(textFieldReference.current.value);
        }
    };

    const cleanBox = (): void => {
        textFieldReference.current.value = "";
        refine("");
        clear();
    };

    useEffect(() => {
        textFieldReference.current.value = query;
    }, [query]);

    useEffect(() => {
        if (queryStringAware) {
            if ("URLSearchParams" in window) {
                const searchParams = new URLSearchParams(window?.location?.search);
                const searchQuery = searchParams.get("q");

                if (searchQuery) {
                    refine(searchQuery);
                }
            }
        }
    }, [location]);

    const inputProps = showSearchButton
        ? {
              variant: "normal" as TextFieldVariant,
              border: true,
          }
        : {
              variant: "dark" as TextFieldVariant,
              border: false,
              startAdornment: <Icon color="text-dark" icon={mdiMagnify} />,
              endAdornment: (
                  <>
                      {query !== "" && (
                          <button
                              onClick={cleanBox}
                              className={style.closeIcon}
                              aria-label={t("Clear search", { ns: "search" })}
                          >
                              <Icon size="small" color="black" icon={mdiClose} />
                          </button>
                      )}
                  </>
              ),
          };

    return (
        <div className={style.formWrapper}>
            <div
                className={classnames(
                    style.searchBox,
                    showSearchButton ? style.half : style.full,
                    style[`${variant}Variant`],
                )}
            >
                <TextField
                    {...inputProps}
                    name="customSearchBox"
                    id="custom-search-box"
                    ref={textFieldReference}
                    data-test-id={"CustomSearchBox"}
                    type="text"
                    hasFocus={hasFocus}
                    placeholder={placeholder}
                    aria-label={t("Search", { ns: "search" })}
                    onChange={onChange}
                    variant="white"
                    border
                    onKeyDown={(e: KeyboardEvent): void => keyHandler(e.key, () => search())}
                />
            </div>
            {showSearchButton && (
                <div className={style.searchButton}>
                    <Button size="large" color="primary" onClick={search}>
                        <Typography font="text" color="white" weight="600">
                            {t("Search", { ns: "search" })}
                        </Typography>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CustomSearchBox;
